import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@rickylandino/react-messages';
import { Table } from 'antd';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import NumberFormat from 'react-number-format';
import Moment from 'moment';
import { ExternalLink } from 'lucide-react';
import Globals from '../../../../config/globals';
import { useExamsWithoutResultsFiltered, useExamsWithoutResultsFilteredCount, invalidateAllWidgetQueries } from '../../../../hooks/useWidgetData';
import { useSaveWidgetFilters } from '../../../../hooks/useDashboard';
import PatientExamPane from '../../../Exams/PatientExamPane';
import { calculateAge } from '../../../Helpers/PatientHelper';
import { getPatientExamResults } from '../../../../common/services/ExamService';
import { useSelectedDashboardFacility } from '../../../../hooks/useSelectedDashboardFacility';

/**
 * ExamsWithoutResultsFilteredWidget component
 * User-level widget showing exams without results filtered by date range
 * @param {Object} props
 * @param {boolean} [props.isCollapsed=false] - Whether the widget is collapsed
 * @param {boolean} [props.isExpanded=false] - Whether the widget is in fullscreen mode
 * @param {Function} [props.setIsLoading] - Callback to set loading state for parent
 * @param {Function} [props.setSharedState] - Callback to update shared state (including refetch)
 * @param {Object} [props.widget] - Widget configuration object
 */
export default function ExamsWithoutResultsFilteredWidget({
    isCollapsed = false,
    isExpanded = false,
    setIsLoading,
    setSharedState,
    widget
}) {
    const saveFiltersMutation = useSaveWidgetFilters();
    const queryClient = useQueryClient();
    const isInitialLoad = useRef(true);

    const { facilities: allFacilities, selectedFacilities } = useSelectedDashboardFacility();

    const [selectedDates, setSelectedDates] = useState({ startDate: null, endDate: null });
    const [queryParams, setQueryParams] = useState(null);

    const [showAddExamPane, setShowAddExamPane] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [selectedPatientExam, setSelectedPatientExam] = useState(null);
    const [examResults, setExamResults] = useState(null);
    const [sharedFollowUpIDs, setSharedFollowUpIDs] = useState(null);
    const [facility, setFacility] = useState(null);

    // Parse saved filter config
    const savedFilters = useMemo(() => {
        if (!widget?.filterConfig) return null;
        if (typeof widget.filterConfig === 'string') {
            try { return JSON.parse(widget.filterConfig); } catch { return null; }
        }
        return widget.filterConfig;
    }, [widget?.filterConfig]);

    const filtersConfigured = selectedFacilities.length > 0;

    const collapsedCountParams = useMemo(() => {
        if (!filtersConfigured) return null;
        return {
            startDate: savedFilters?.startDate || '',
            endDate: savedFilters?.endDate || '',
            ianaTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            selectedFacilities
        };
    }, [savedFilters, filtersConfigured, selectedFacilities]);

    const { data: collapsedCountData, isLoading: isLoadingCollapsedCount } = useExamsWithoutResultsFilteredCount(
        collapsedCountParams,
        isCollapsed && !isExpanded && filtersConfigured
    );

    const shouldLoadData = !isCollapsed || isExpanded;

    const {
        data: results = [],
        isLoading,
        isFetching,
        isError,
        error,
        refetch
    } = useExamsWithoutResultsFiltered(queryParams, shouldLoadData && queryParams !== null);

    // Notify parent of loading state
    useEffect(() => {
        if (setIsLoading && isExpanded) {
            setIsLoading(isLoading || isFetching);
        }
    }, [isLoading, isFetching, isExpanded, setIsLoading]);

    // Show error toast
    useEffect(() => {
        if (shouldLoadData && isError) {
            toast.error(`Failed to load exams: ${error?.message || 'Unknown error'}`);
        }
    }, [shouldLoadData, isError, error]);

    // Set refetch in shared state
    useEffect(() => {
        if (setSharedState && refetch) {
            setSharedState(prev => ({ ...prev, refetch }));
        }
    }, [setSharedState, refetch]);

    useEffect(() => {
        if (setSharedState) setSharedState(prev => ({ ...prev, hasData: queryParams !== null || filtersConfigured }));
    }, [queryParams, filtersConfigured, setSharedState]);

    // Restore saved date filters on initial load. Facility now comes from the global
    // dashboard facility selector, not per-widget saved config.
    useEffect(() => {
        if (!isInitialLoad.current || !savedFilters) return;
        isInitialLoad.current = false;
        if (savedFilters.startDate || savedFilters.endDate) {
            setSelectedDates({
                startDate: savedFilters.startDate ? Moment(savedFilters.startDate).toDate() : null,
                endDate: savedFilters.endDate ? Moment(savedFilters.endDate).toDate() : null
            });
        }
    }, [savedFilters]);

    // Auto-trigger query when transitioning out of collapsed state once a global facility is selected
    useEffect(() => {
        if ((isCollapsed && !isExpanded) || queryParams !== null) return;
        if (selectedFacilities.length === 0) return;

        setQueryParams({
            startDate: savedFilters?.startDate || '',
            endDate: savedFilters?.endDate || '',
            ianaTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            selectedFacilities
        });
    }, [isCollapsed, isExpanded, savedFilters, queryParams, selectedFacilities]);

    const handleNewDateChange = useCallback((name, date) => {
        setSelectedDates(prev => ({ ...prev, [name]: date }));
    }, []);

    const handleSubmit = useCallback((e) => {
        e?.preventDefault();

        if (selectedFacilities.length === 0) {
            toast.error('Please select a facility using the Facility selector above.');
            return;
        }

        const startDate = selectedDates.startDate ? Moment(selectedDates.startDate).format('YYYY-MM-DD') : null;
        const endDate = selectedDates.endDate ? Moment(selectedDates.endDate).format('YYYY-MM-DD') : null;

        const params = {
            startDate: startDate || '',
            endDate: endDate || '',
            ianaTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            selectedFacilities
        };

        if (widget?.userDashboardWidgetId) {
            saveFiltersMutation.mutate({
                userDashboardWidgetId: widget.userDashboardWidgetId,
                filterConfig: { startDate, endDate, facilityIds: selectedFacilities }
            });
        }

        queryClient.invalidateQueries({ queryKey: ['widgetData', 'exams-without-results-filtered'] });
        setQueryParams(params);
    }, [selectedDates, selectedFacilities, widget?.userDashboardWidgetId, saveFiltersMutation, queryClient]);

    const handleViewExam = useCallback(async (row) => {
        try {
            const userInfo = JSON.parse(window.sessionStorage.getItem("userInfo"));

            if (!userInfo?.userId) {
                toast.error('User information not found. Please log in again.');
                return;
            }

            if (!row.patientExamId) {
                toast.error('Missing exam information');
                return;
            }

            if (!row.facilityId) {
                toast.warning('Facility information missing from API. Please contact support.');
                console.error('Missing facilityId in API response. Row:', row);
                return;
            }

            const postdata = {
                uniqueID: row.patientExamId,
                facilityID: row.facilityId,
                name: row.examClass,
                userID: userInfo.userId
            };

            const data = await getPatientExamResults(postdata);

            if (!data) {
                throw new Error('No data returned from server');
            }

            setSelectedPatient({ dob: row.patientDob || row.dob, patientName: row.patientName, mrn: row.mrn });
            setSelectedPatientExam(data.examInfo);
            setExamResults(data.resultInfo);
            setSharedFollowUpIDs(data.examIDs);
            setFacility({ facilityID: row.facilityId, facilityName: row.facilityName });
            setShowAddExamPane(true);

        } catch (error) {
            console.error('Error loading exam:', error);
            toast.error(`Failed to load exam: ${error?.message || 'Unknown error'}`);
        }
    }, []);

    const updateExamInfo = useCallback(() => {
        toast.destroy('loading');
        toast.destroy('updating');
        setShowAddExamPane(false);
        // Invalidate all widget queries since exam changes can affect multiple widgets
        invalidateAllWidgetQueries(queryClient);
        toast.success('Widget data refreshed');
    }, [queryClient]);

    const getNewExamInfo = useCallback(() => {
        toast.destroy('loading');
        toast.destroy('updating');
        setShowAddExamPane(false);
        // Invalidate all widget queries since exam changes can affect multiple widgets
        invalidateAllWidgetQueries(queryClient);
        toast.success('Widget data refreshed');
    }, [queryClient]);

    /**
     * Format date to short format
     */
    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'numeric', day: 'numeric', year: 'numeric'
        });
    }, []);

    /**
     * Format MRN with spaces
     */
    const formatMRN = useCallback((mrn) => {
        if (!mrn) return '';
        return mrn.replace(/(.{10})/g, '$1 ').trim();
    }, []);

    /**
     * Calculate days since exam
     */
    const getDaysSinceExam = useCallback((examDateTime) => {
        const diffMs = new Date() - new Date(examDateTime);
        return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    }, []);

    const columns = useMemo(() => [
        {
            title: 'Patient Name',
            dataIndex: 'patientName',
            key: 'patientName',
            sorter: (a, b) => (a.patientName || '').localeCompare(b.patientName || ''),
            width: 200,
        },
        {
            title: 'MRN',
            dataIndex: 'mrn',
            key: 'mrn',
            render: (mrn) => <span style={{ fontFamily: 'monospace' }}>{formatMRN(mrn)}</span>,
            width: 150,
        },
        {
            title: 'DOB',
            dataIndex: 'dob',
            key: 'dob',
            render: (dob) => formatDate(dob),
            sorter: (a, b) => new Date(a.dob) - new Date(b.dob),
            width: 120,
        },
        {
            title: 'Provider',
            dataIndex: 'providerName',
            key: 'providerName',
            sorter: (a, b) => (a.providerName || '').localeCompare(b.providerName || ''),
            width: 150,
        },
        {
            title: 'Facility',
            dataIndex: 'facilityName',
            key: 'facilityName',
            sorter: (a, b) => (a.facilityName || '').localeCompare(b.facilityName || ''),
            width: 150,
        },
        {
            title: 'Exam Date',
            dataIndex: 'examDateTime',
            key: 'examDateTime',
            render: (examDateTime) => formatDate(examDateTime),
            sorter: (a, b) => new Date(a.examDateTime) - new Date(b.examDateTime),
            defaultSortOrder: 'ascend',
            width: 120,
        },
        {
            title: 'Exam Type',
            dataIndex: 'examType',
            key: 'examType',
            sorter: (a, b) => (a.examType || '').localeCompare(b.examType || ''),
            width: 150,
        },
        {
            title: 'Days Ago',
            key: 'daysAgo',
            render: (_, record) => {
                const daysAgo = getDaysSinceExam(record.examDateTime);
                const isUrgent = daysAgo > 30;
                return (
                    <span style={{
                        color: isUrgent
                            ? (Globals.theme === 'dark' ? '#f87171' : '#dc2626')
                            : (Globals.theme === 'dark' ? '#fb923c' : '#ea580c'),
                        fontWeight: 500
                    }}>
                        {daysAgo}
                    </span>
                );
            },
            sorter: (a, b) => getDaysSinceExam(a.examDateTime) - getDaysSinceExam(b.examDateTime),
            width: 100,
        },
        {
            title: 'Actions',
            key: 'action',
            width: 100,
            fixed: 'right',
            render: (_, row) => (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleViewExam(row);
                    }}
                    className="px-3 py-1.5 rounded-lg font-medium text-sm transition-colors inline-flex items-center gap-1"
                    style={{
                        backgroundColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777',
                        color: 'white',
                        border: 'none',
                        cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                            Globals.theme === 'dark' ? '#db2777' : '#be185d';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                            Globals.theme === 'dark' ? '#ec4899' : '#db2777';
                    }}
                    title="View exam"
                >
                    View
                    <ExternalLink size={14} />
                </button>
            )
        },
    ], [formatDate, formatMRN, getDaysSinceExam, handleViewExam]);

    // COLLAPSED STATE
    if (isCollapsed && !isExpanded) {
        if (filtersConfigured) {
            if (isLoadingCollapsedCount) {
                return (
                    <div className="flex items-center justify-center py-4">
                        <div
                            className="animate-spin rounded-full h-8 w-8 border-b-2"
                            style={{ borderColor: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                        />
                    </div>
                );
            }
            const count = collapsedCountData?.count ?? 0;
            return (
                <div className="flex flex-col items-center justify-center py-4">
                    <div
                        className="text-5xl font-light mb-2"
                        style={{ color: Globals.theme === 'dark' ? '#ec4899' : '#db2777' }}
                    >
                        {count.toLocaleString()}
                    </div>
                    <div
                        className="text-sm"
                        style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                    >
                        {count === 1 ? 'exam without results' : 'exams without results'}
                    </div>
                </div>
            );
        }
        return (
            <div className="flex flex-col items-center justify-center py-4">
                <div className="text-sm" style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>
                    Expand to configure this widget
                </div>
            </div>
        );
    }

    return (
        <>
        <div className="space-y-4">
            {/* Filter Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <div className="form-label">
                            Start Range
                        </div>
                        <DatePicker
                            onChange={(date) => handleNewDateChange('startDate', date)}
                            selectsStart
                            startDate={selectedDates.startDate}
                            endDate={selectedDates.endDate}
                            className="form-control-custom w-full"
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            placeholderText="Start Date"
                            selected={selectedDates.startDate && Moment(selectedDates.startDate).toDate()}
                            customInput={<NumberFormat format="##/##/####" mask="_" />}
                        />
                    </div>
                    <div>
                        <div className="form-label">
                            End Range
                        </div>
                        <DatePicker
                            onChange={(date) => handleNewDateChange('endDate', date)}
                            selectsEnd
                            startDate={selectedDates.startDate}
                            endDate={selectedDates.endDate}
                            className="form-control-custom w-full"
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            placeholderText="End Date"
                            selected={selectedDates.endDate && Moment(selectedDates.endDate).toDate()}
                            customInput={<NumberFormat format="##/##/####" mask="_" />}
                        />
                    </div>
                    <div>
                        <div className="form-label">
                            Facility
                        </div>
                        <div className="form-control-custom">
                            {allFacilities.find(f => f.facilityModel.facilityID === selectedFacilities[0])?.facilityModel.facilityName
                                || 'Select a facility using the selector above'}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={queryParams && isFetching}
                        className="px-4 py-2 rounded-lg font-medium transition-colors"
                        style={{
                            backgroundColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777',
                            color: 'white',
                            opacity: (queryParams && isFetching) ? 0.6 : 1
                        }}
                        onMouseEnter={(e) => {
                            if (!(queryParams && isFetching)) {
                                e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#db2777' : '#be185d';
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#ec4899' : '#db2777';
                        }}
                    >
                        {(queryParams && isFetching) ? 'Loading...' : 'Generate Report'}
                    </button>
                </div>
            </form>

            {/* Results */}
            {queryParams && (
                <>
                    {results.length === 0 && !isLoading ? (
                        <div className="text-center py-8 text-sm" style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>
                            No exams without results found for the selected filters
                        </div>
                    ) : (
                        <>
                            <div className="text-sm" style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>
                                Results (<span style={{ color: Globals.theme === 'dark' ? '#e4e4e7' : '#18181b' }}>{results.length} exam{results.length !== 1 ? 's' : ''}</span>)
                            </div>
                            <Table
                                columns={columns}
                                dataSource={results}
                                rowKey="patientExamId"
                                pagination={{
                                    pageSize: 50,
                                    pageSizeOptions: ['25', '50', '100'],
                                    showSizeChanger: true,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} exams`
                                }}
                                size="small"
                                loading={isLoading}
                                scroll={{ y: 500 }}
                                virtual
                            />
                        </>
                    )}
                </>
            )}
        </div>

        {showAddExamPane && selectedPatient && (
            <PatientExamPane
                patientAge={calculateAge(selectedPatient?.dob)}
                patientInactive={false}
                patient={selectedPatient}
                showPane={showAddExamPane}
                examInfo={selectedPatientExam}
                examHistory={examResults}
                facility={facility}
                updateExamInfo={updateExamInfo}
                from='no-results-entered'
                hidePane={() => setShowAddExamPane(false)}
                sharedFollowUpIDs={sharedFollowUpIDs}
                getNewExamInfo={getNewExamInfo}
            />
        )}
        </>
    );
}
